/**
 * Solfège — movable-do system with chromatic syllables.
 *
 * The engine maps scale degrees (1-7) and chromatic alterations to syllables.
 * Two modes are supported:
 *
 *   MAJOR keys:              do re mi fa sol la ti
 *   MINOR keys (do-based):   do re me fa sol le te  (root = do — default)
 *   MINOR keys (la-based):   la ti do re mi fa sol  (root = la — alternative)
 *
 * Chromatic syllables follow standard pedagogical convention:
 *   raised: di, ri, fi, si, li
 *   lowered: ra, me, se, le, te
 *
 * Do-based minor keeps the tonic as "do" across all modes, which is optimal
 * for functional ear training. La-based minor is supported as an option for
 * users trained in the Kodály/Curwen tradition.
 */

import { type Pitch, pitchClass } from "./pitch.js";
import { type Scale, scalePitches } from "./scale.js";

export type SolfegeMode = "la-based" | "do-based";

/**
 * Movable-do syllables, indexed by semitones above tonic (0-11).
 *
 * Major key mapping. For la-based minor, we relabel: la=0, ti=2, do=3, etc.
 * Each slot has both raised and lowered names where applicable; we pick contextually.
 */
const MAJOR_SYLLABLES_BY_SEMITONE: Record<number, { natural: string; raised?: string; lowered?: string }> = {
  0:  { natural: "do" },
  1:  { natural: "do", raised: "di", lowered: "ra" },   // di = raised do, ra = lowered re
  2:  { natural: "re" },
  3:  { natural: "re", raised: "ri", lowered: "me" },   // ri = raised re, me = lowered mi
  4:  { natural: "mi" },
  5:  { natural: "fa" },
  6:  { natural: "fa", raised: "fi", lowered: "se" },   // fi = raised fa, se = lowered sol
  7:  { natural: "sol" },
  8:  { natural: "sol", raised: "si", lowered: "le" },  // si = raised sol, le = lowered la
  9:  { natural: "la" },
  10: { natural: "la", raised: "li", lowered: "te" },   // li = raised la, te = lowered ti
  11: { natural: "ti" },
};

/** The seven natural diatonic syllables in major. */
export const MAJOR_DIATONIC_SYLLABLES = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;

/**
 * Do-based minor: fixed syllables for each diatonic semitone above the minor tonic.
 * Chromatic positions (1, 4, 6, 9, 11) fall through to the major syllable table.
 */
const MINOR_DO_BASED_DIATONIC: Record<number, string> = {
  0: "do", 2: "re", 3: "me", 5: "fa", 7: "sol", 8: "le", 10: "te",
};

/**
 * Return the solfège syllable for a given pitch in a given key context.
 *
 * Strategy:
 * 1. Compute semitones above tonic.
 * 2. For do-based minor: diatonic positions use a fixed minor table; chromatic
 *    positions fall through to the major table with accidental-based selection.
 * 3. For la-based minor: shift by 9 semitones (minor tonic → "la") then use
 *    the major table.
 * 4. For major (or chromatic minor): use the major table with accidental-based
 *    selection (e.g. F# in C major → "fi"; Gb → "se").
 */
export function pitchToSolfege(
  p: Pitch,
  keyTonic: Pitch,
  isMinorKey = false,
  mode: SolfegeMode = "do-based",
): string {
  const semitonesAboveTonic = ((pitchClass(p) - pitchClass(keyTonic)) + 12) % 12;

  // Do-based minor: diatonic notes use the minor-specific fixed syllables.
  // Chromatic notes (not in the table) fall through to the major table below.
  if (isMinorKey && mode === "do-based") {
    const diatonic = MINOR_DO_BASED_DIATONIC[semitonesAboveTonic];
    if (diatonic !== undefined) return diatonic;
  }

  // La-based minor: shift by 9 semitones so the minor tonic maps to slot 9 (= "la").
  const effectiveSemitone = isMinorKey && mode === "la-based"
    ? (semitonesAboveTonic + 9) % 12
    : semitonesAboveTonic;

  const slot = MAJOR_SYLLABLES_BY_SEMITONE[effectiveSemitone]!;

  // Prefer the right alteration based on the pitch's accidental sign.
  if (slot.raised || slot.lowered) {
    if (p.accidental > 0 && slot.raised) return slot.raised;
    if (p.accidental < 0 && slot.lowered) return slot.lowered;
  }
  return slot.natural;
}

/**
 * Solfège syllables for an entire scale, in order.
 * For a major scale: do, re, mi, fa, sol, la, ti
 * For natural minor (do-based, default): do, re, me, fa, sol, le, te
 * For natural minor (la-based): la, ti, do, re, mi, fa, sol
 */
export function scaleSolfege(s: Scale, isMinorKey = false, mode: SolfegeMode = "do-based"): string[] {
  return scalePitches(s).map((p) => pitchToSolfege(p, s.root, isMinorKey, mode));
}

/**
 * All possible chromatic syllables (12 ascending), starting from do.
 * Useful for UI buttons that show the full chromatic palette.
 */
export const CHROMATIC_SYLLABLES_ASCENDING = [
  "do", "di", "re", "ri", "mi", "fa", "fi", "sol", "si", "la", "li", "ti",
] as const;

export const CHROMATIC_SYLLABLES_DESCENDING = [
  "do", "ti", "te", "la", "le", "sol", "se", "fa", "mi", "me", "re", "ra",
] as const;
